// Every job this console has ever sent.
//
// The board is today and stays that way. This is the other question —
// "what happened at that address last Tuesday", "what did we send that
// week", "show me the proof for the one they are complaining about" —
// and it is a search rather than a board.
//
// It starts at the **first** job rather than at yesterday's. An operator
// who sent three deliveries this morning has a history of three
// deliveries, and a screen that told them there was nothing to see until
// tomorrow would be wrong twice: about the record, and about what they
// came here to do.
//
// What it will not do is score anybody. The protocol makes a driver's
// day legible to their dispatcher because the work requires it; how fast
// they were is not a column here, and DESIGN says why.

import { useMemo, useState } from "react";
import { STOP_OUTCOME, type FollowSnapshot } from "@wayfind/core";
import { Panel } from "./Bits";
import type { FleetState, StoredJob } from "../ops/state";
import { clockWords, dateWords, plural, type Vocabulary } from "../ops/words";
import { nowSeconds } from "../config";

const DAY = 86400;

type Filter = "all" | "open" | "settled" | "trouble";

/** The day a job belongs to, as a heading a person would write. */
function dayKey(unixSeconds: number): string {
  const date = new Date(unixSeconds * 1000);
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

function dayWords(unixSeconds: number, now: number): string {
  const days = Math.floor((now - unixSeconds) / DAY);
  if (dayKey(unixSeconds) === dayKey(now)) return "Today";
  if (days <= 1 && dayKey(unixSeconds) === dayKey(now - DAY)) return "Yesterday";
  return dateWords(unixSeconds);
}

export function History({
  state,
  live,
  vocabulary,
  onOpen
}: {
  state: FleetState;
  live: Map<string, FollowSnapshot>;
  vocabulary: Vocabulary;
  onOpen(job: StoredJob): void;
}): JSX.Element {
  const now = nowSeconds();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");

  const matched = useMemo(() => {
    const text = query.trim().toLowerCase();
    return state.jobs
      .slice()
      .sort((a, b) => b.createdUnixSeconds - a.createdUnixSeconds)
      .filter(job => {
        if (filter === "open" && job.finishedUnixSeconds != null) return false;
        if (filter === "settled" && job.finishedUnixSeconds == null) return false;
        if (filter === "trouble") {
          const outcomes = live.get(job.id)?.status.outcomes ?? [];
          const missed = outcomes.some(
            outcome => outcome === STOP_OUTCOME.SKIPPED || outcome === STOP_OUTCOME.FAILED
          );
          if (!missed) return false;
        }
        if (!text) return true;
        return job.stops.some(stop => stop.label.toLowerCase().includes(text))
          || (job.driverName ?? "").toLowerCase().includes(text)
          || (state.devices.find(device => device.id === job.deviceId)?.name ?? "")
            .toLowerCase().includes(text)
          || job.jobIdHex.toLowerCase().startsWith(text);
      });
  }, [state.jobs, state.devices, live, query, filter]);

  // Grouped by day, because that is how anybody looking for one asks:
  // "it was Tuesday" long before "it was the 14th".
  const days = useMemo(() => {
    const groups: { key: string; label: string; jobs: StoredJob[] }[] = [];
    for (const job of matched) {
      const key = dayKey(job.createdUnixSeconds);
      const last = groups[groups.length - 1];
      if (last && last.key === key) last.jobs.push(job);
      else groups.push({ key, label: dayWords(job.createdUnixSeconds, now), jobs: [job] });
    }
    return groups;
  }, [matched, now]);

  return (
    <div className="stack">
      <Panel eyebrow="Everything sent">
        <div className="searchrow">
          <input
            className="input"
            value={query}
            placeholder={`An address, a ${vocabulary.driver}, or a job id`}
            onChange={event => setQuery(event.target.value)}
            aria-label="Search what was sent"
          />
          <div className="segmented" role="group" aria-label="Filter">
            {([
              ["all", "All"],
              ["open", "Out"],
              ["settled", "Finished"],
              ["trouble", "Not delivered"]
            ] as [Filter, string][]).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`segmented__button${filter === value ? " segmented__button--on" : ""}`}
                onClick={() => setFilter(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <p className="small muted" style={{ marginTop: "0.5rem" }}>
          {plural(matched.length, vocabulary.job)}
          {state.jobs.length !== matched.length ? ` of ${state.jobs.length}` : ""}
          {" · this console remembers what it sent, and nothing was reported to a server"}
        </p>
      </Panel>

      {days.map(day => (
        <Panel key={day.key} eyebrow={day.label}>
          <div className="rows">
            {day.jobs.map(job => {
              const snapshot = live.get(job.id) ?? null;
              const outcomes = snapshot?.status.outcomes ?? [];
              const delivered = outcomes.filter(o => o === STOP_OUTCOME.DELIVERED).length;
              const missed = outcomes.filter(
                o => o === STOP_OUTCOME.SKIPPED || o === STOP_OUTCOME.FAILED
              ).length;
              const who = state.devices.find(device => device.id === job.deviceId)?.name
                ?? job.driverName
                ?? "—";
              return (
                <button
                  key={job.id}
                  type="button"
                  className={`historyrow${missed ? " historyrow--halt" : ""}`}
                  onClick={() => onOpen(job)}
                >
                  <span className="historyrow__time">{clockWords(job.createdUnixSeconds)}</span>
                  <span className="historyrow__what">
                    {job.stops.length === 1
                      ? job.stops[0]!.label
                      : `${job.stops[0]!.label} → ${plural(job.stops.length, vocabulary.stop)}`}
                  </span>
                  <span className="historyrow__who">{who}</span>
                  <span className="historyrow__outcome">
                    {/*
                      Counts appear only where this console re-read the
                      run. A settled job is not followed any more, so an
                      absent count means "not re-read" — never "nothing
                      was delivered", which is why a zero is not drawn.
                    */}
                    {delivered || missed ? (
                      <>
                        <b>{delivered}</b> delivered{missed ? `, ${missed} not` : ""}
                      </>
                    ) : job.finishedUnixSeconds != null ? "closed" : "out"}
                  </span>
                </button>
              );
            })}
          </div>
        </Panel>
      ))}

      {!days.length ? (
        <Panel>
          <p className="small muted">
            {state.jobs.length
              ? "Nothing matches that."
              : `Nothing has been sent from this console yet. The first ${vocabulary.job} you send appears here.`}
          </p>
        </Panel>
      ) : null}
    </div>
  );
}
